import React, { useEffect, useState } from 'react';

import { Ellipsis, Loader } from 'lucide-react';
import { useInView } from 'react-intersection-observer';

import { CompanyDetailsSheet, Filters, SortableTableHeader, CompanySocialLinks } from '#/common/components';
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
    Badge,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from '#/components/ui';
import { useAppContext } from '#/context';
import { apiEndpoints, queryKeys, useInfiniteScrollQuery } from '#/services';
import {
    capitalizeWords,
    getAddressByType,
    COMPANIES_TABLE_HEADERS,
    COMPANY_TABLE_COL_GROUP,
    ADDRESS_TYPES,
} from '#/utils';
import { CompaniesNotFound } from '..';

const Companies = ({ companyFilters }) => {
    const { ref, inView } = useInView();
    const [queryParams, setQueryParams] = useState({});
    const { companiesQueryParams, setCompaniesQueryParams } = useAppContext();

    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteScrollQuery({
        key: queryKeys.COMPANIES,
        url: apiEndpoints.GET_COMPANIES,
        params: queryParams,
    });

    useEffect(() => {
        if (companiesQueryParams && Object?.keys(companiesQueryParams).length !== 0) {
            setQueryParams(companiesQueryParams);
            setCompaniesQueryParams({});
        }
    }, [companiesQueryParams]);

    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [inView, hasNextPage, fetchNextPage, isFetchingNextPage]);

    const companies = data?.pages.flatMap(page => page.results) ?? [];

    const handleClear = () => {
        setQueryParams({});
    };

    const [selectedCompany, setSelectedCompany] = useState(null);
    const [isSheetOpen, setIsSheetOpen] = useState(false);

    const handleRowClick = company => {
        setSelectedCompany(company);
        setIsSheetOpen(true);
    };

    return (
        <>
            <Filters
                queryParams={queryParams}
                filtersData={companyFilters}
                setQueryParams={setQueryParams}
                onClearFilters={handleClear}
                addCompany={true}
            />

            <div className='overflow-x-auto'>
                <Table className='w-full table-fixed'>
                    {COMPANY_TABLE_COL_GROUP}
                    <TableHeader className='bg-white sticky top-0 z-10'>
                        <SortableTableHeader headers={COMPANIES_TABLE_HEADERS} setQueryParams={setQueryParams} />
                    </TableHeader>
                </Table>

                <div className='h-[calc(100dvh-400px)] xl:h-[calc(100dvh-310px)] overflow-y-auto pl-2'>
                    <Table className='w-full table-fixed'>
                        {COMPANY_TABLE_COL_GROUP}
                        <TableBody className='w-full'>
                            {isLoading ? (
                                <TableRow className='border-none'>
                                    <TableCell colSpan={5} className='h-[300px] text-center'>
                                        <Loader className='mx-auto animate-spin text-muted-foreground' />
                                    </TableCell>
                                </TableRow>
                            ) : companies?.length > 0 ? (
                                companies?.map(
                                    ({
                                        id,
                                        name,
                                        profilePicture,
                                        businessType,
                                        businessTypeBackgroundColor,
                                        businessTypeTextColor,
                                        addresses,
                                        socialLinks,
                                    }) => {
                                        return (
                                            <TableRow
                                                key={id}
                                                onClick={() => handleRowClick({ id })}
                                                className='h-[72px]'
                                            >
                                                <TableCell className='h-[72px] flex items-center gap-2'>
                                                    <Avatar className='w-10 h-10'>
                                                        <AvatarImage src={profilePicture} alt={name} />
                                                        <AvatarFallback className='bg-gray200 text-gray500'>
                                                            {name?.charAt(0)}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                    <span className='whitespace-normal break-words max-w-xs text-gray900 text-sm font-medium'>
                                                        {capitalizeWords(name)}
                                                    </span>
                                                </TableCell>
                                                <TableCell className='pl-3'>
                                                    {businessType ? (
                                                        <Badge
                                                            className='flex gap-2 w-fit capitalize rounded-full text-[14px]'
                                                            style={{
                                                                backgroundColor: businessTypeBackgroundColor,
                                                                color: businessTypeTextColor,
                                                            }}
                                                        >
                                                            <div className='w-2 h-2 rounded-full bg-current'></div>
                                                            {businessType}
                                                        </Badge>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </TableCell>
                                                <TableCell className='pl-4 text-gray500'>
                                                    {(() => {
                                                        const headquarter = getAddressByType(
                                                            addresses,
                                                            ADDRESS_TYPES.HEADQUARTER
                                                        );
                                                        if (!headquarter) return '-';

                                                        const { region, country } = headquarter;
                                                        return (
                                                            <>
                                                                {region}
                                                                {region && ', '}
                                                                {country || '-'}
                                                            </>
                                                        );
                                                    })()}
                                                </TableCell>

                                                <TableCell className='pl-3' onClick={e => e.stopPropagation()}>
                                                    <CompanySocialLinks socialLinks={socialLinks} />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    }
                                )
                            ) : (
                                <TableRow className='border-none hover:bg-white'>
                                    <TableCell colSpan={2} className='border-none'>
                                        <CompaniesNotFound />
                                    </TableCell>
                                </TableRow>
                            )}
                            <TableRow className='pointer-events-none'>
                                <TableCell colSpan={5}>
                                    <div ref={ref} id='sentinel' className='py-6 flex justify-center text-center'>
                                        {isFetchingNextPage && <Ellipsis className='animate-ping' />}
                                    </div>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </div>
            </div>

            {isSheetOpen && (
                <CompanyDetailsSheet
                    isSheetOpen={isSheetOpen}
                    setIsSheetOpen={setIsSheetOpen}
                    selectedCompany={selectedCompany}
                />
            )}
        </>
    );
};

export { Companies };
